import { FloatingPortal, autoUpdate, flip, offset, shift, size, useClick, useDismiss, useFloating, useHover, useInteractions } from '@floating-ui/react';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useConfig } from '../../../../../../contexts/config-context';
import { CoreDropdownContent } from '../CoreDropdownContent';
/**
 * Intended to gradually replace CoreDropdownComponent across all WUT fields.
 * Does not have full feature parity yet.
 */ export const FloatingDropdown = /*#__PURE__*/ forwardRef(({ children, placement, options, initialSelectedIds, allowReselect = false, focusOnOption, onSelect, onOptionHover, onExpandedChange, fixedHeader, openTrigger = 'click', disabled = false, readOnly = false, noResultsText, className, contentClassName, dir, dropdownContentClassName, contentId, contentRole, zIndex = 'var(--portals-z-index, 1000)', matchTriggerWidth = false, contentOffset = 0, onContentMouseDown }, ref)=>{
    const contentRef = useRef(null);
    // The component has two competing requirements:
    // 1. Escape overflow:hidden clipping → portal content to document.body
    // 2. Inherit CSS vars (--wix-forms-*, TPA vars) → stay inside div.{compId} subtree
    // Solution: portal to a custom root div with the compId class.
    const { compId, PortalContainer, isRTL } = useConfig();
    const Container = PortalContainer ?? React.Fragment;
    // useState instead of useRef: FloatingPortal locks in its root on first mount.
    // If root is null on first render it defaults to document.body — wrong element.
    // State delays mounting until the root div exists, so FloatingPortal always
    // gets the compId-classed element. useLayoutEffect = no flash before it's ready.
    const [portalRoot, setPortalRoot] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState(()=>initSelectedIds(initialSelectedIds, options));
    useLayoutEffect(()=>{
        const el = document.createElement('div');
        if (compId) {
            el.classList.add(compId, `${compId}_ooi`);
        }
        document.body.appendChild(el);
        setPortalRoot(el);
        return ()=>{
            if (document.body.contains(el)) {
                document.body.removeChild(el);
            }
        };
    }, [
        compId
    ]);
    // Sync selectedIds when initialSelectedIds/options changes (e.g. controlled parent).
    useEffect(()=>{
        setSelectedIds((prev)=>{
            const next = initSelectedIds(initialSelectedIds, options);
            if (prev.length === next.length && prev.every((id, i)=>id === next[i])) {
                return prev;
            }
            return next;
        });
    }, [
        initialSelectedIds,
        options
    ]);
    const handleOpenChange = useCallback((open)=>{
        setIsOpen(open);
        onExpandedChange?.(open);
    }, [
        onExpandedChange
    ]);
    const handleOptionClick = useCallback((option)=>{
        if (!option) {
            setSelectedIds([]);
            handleOpenChange(false);
            return;
        }
        if (!allowReselect && selectedIds.includes(option.id)) {
            handleOpenChange(false);
            return;
        }
        setSelectedIds([
            option.id
        ]);
        onSelect(option);
        handleOpenChange(false);
    }, [
        allowReselect,
        selectedIds,
        onSelect,
        handleOpenChange
    ]);
    const middlewares = useMemo(()=>[
            offset(contentOffset),
            flip(),
            size({
                apply ({ availableWidth, rects, elements }) {
                    Object.assign(elements.floating.style, {
                        maxWidth: `${availableWidth}px`,
                        ...matchTriggerWidth && {
                            // Together with the panel's -1px left margin, extends each edge by 1px.
                            width: `${rects.reference.width + 2}px`
                        }
                    });
                }
            }),
            shift()
        ], [
        matchTriggerWidth,
        contentOffset
    ]);
    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: handleOpenChange,
        placement,
        middleware: middlewares,
        whileElementsMounted: autoUpdate
    });
    const isInteractive = !disabled && !readOnly;
    const click = useClick(context, {
        enabled: isInteractive && openTrigger === 'click',
        // Native buttons handle keyboard clicks; inputs handle their own keys.
        keyboardHandlers: false
    });
    const hover = useHover(context, {
        enabled: isInteractive && openTrigger === 'hover'
    });
    const dismiss = useDismiss(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([
        click,
        hover,
        dismiss
    ]);
    useImperativeHandle(ref, ()=>({
            open () {
                handleOpenChange(true);
            },
            close () {
                handleOpenChange(false);
            },
            getSelectedOption () {
                return contentRef.current?.getSelectedOption() ?? null;
            },
            onKeyDown (event) {
                const content = contentRef.current;
                if (!content) {
                    return;
                }
                const { key } = event;
                if (key === 'Tab' || key === 'Enter') {
                    const selected = content.getSelectedOption();
                    if (selected) {
                        handleOptionClick(selected);
                        event.preventDefault();
                    } else {
                        handleOpenChange(false);
                    }
                } else {
                    content.onKeyDown(key, event);
                }
            }
        }), [
        handleOpenChange,
        handleOptionClick
    ]);
    const shown = isOpen && !disabled && !readOnly;
    const hasContent = Boolean(options?.length || fixedHeader);
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("span", {
        ref: refs.setReference,
        className: className,
        style: {
            display: 'flex',
            height: '100%',
            alignItems: 'center'
        },
        ...getReferenceProps()
    }, children), shown && hasContent && portalRoot && /*#__PURE__*/ React.createElement(FloatingPortal, {
        root: portalRoot
    }, /*#__PURE__*/ React.createElement(Container, null, /*#__PURE__*/ React.createElement("div", {
        ref: refs.setFloating,
        dir: dir ?? (isRTL ? 'rtl' : 'ltr'),
        style: {
            ...floatingStyles,
            zIndex
        },
        className: contentClassName,
        ...getFloatingProps()
    }, /*#__PURE__*/ React.createElement(CoreDropdownContent, {
        ref: contentRef,
        "data-hook": "dropdown-content",
        className: dropdownContentClassName,
        options: options,
        selectedIds: selectedIds,
        fixedHeader: fixedHeader,
        focusOnOption: focusOnOption,
        onMouseDown: onContentMouseDown,
        onOptionClick: handleOptionClick,
        onOptionHover: onOptionHover,
        optionsContainerId: contentId,
        role: contentRole,
        noResultsText: noResultsText
    })))));
});
FloatingDropdown.displayName = 'FloatingDropdown';
function initSelectedIds(ids, options) {
    return (ids ?? []).filter((id)=>{
        const opt = options.find((o)=>o.id === id);
        return opt && !opt.isDisabled && opt.isSelectable;
    });
}

//# sourceMappingURL=floating-dropdown.js.map